import { useWorkOSAuth } from '../lib/auth';
import { useUserOrganizations } from '../lib/useUserOrganizations';
import { BuildingOfficeIcon } from './icons';
import { OrganizationSwitcher } from './OrganizationSwitcher';

interface NoOrganizationProps {
  userEmail?: string;
  reason?: 'not_authenticated' | 'no_org_in_token' | 'no_membership';
}

export function NoOrganization({ userEmail, reason }: NoOrganizationProps) {
  const { signOut } = useWorkOSAuth();
  const organizations = useUserOrganizations();
  // The user has no WorkOS memberships at all
  const hasNoMemberships = !organizations.isLoading && !organizations.error && organizations.memberships.length === 0;

  // Show organization switcher for no_org_in_token (user needs to select an org)
  // Unless they have no memberships at all
  const showOrgSwitcher = reason === 'no_org_in_token' && !hasNoMemberships;

  // Determine the message based on reason and membership status
  const getMessage = () => {
    if (reason === 'no_org_in_token' && hasNoMemberships) {
      return {
        title: 'Kein Verein',
        subtitle: 'Sie sind noch keinem Verein zugewiesen.',
      };
    }

    switch (reason) {
      case 'no_org_in_token':
        return {
          title: 'Verein auswählen',
          subtitle: 'Wählen Sie einen Verein aus, um fortzufahren.',
        };
      case 'no_membership':
        return {
          title: 'Kein Mitglied',
          subtitle: 'Sie sind Mitglied eines Vereins, aber noch nicht als Mitglied in der App registriert.',
        };
      default:
        return {
          title: 'Kein Verein',
          subtitle: 'Sie sind noch keinem Verein zugewiesen.',
        };
    }
  };

  const message = getMessage();

  return (
    <div className="min-h-screen bg-app-gradient flex flex-col items-center justify-center p-8">
      <div className="max-w-md w-full text-center">
        {/* Icon */}
        <div className="mb-8">
          <div className="w-24 h-24 mx-auto bg-white rounded-full shadow-lg flex items-center justify-center mb-4">
            <BuildingOfficeIcon className="w-12 h-12 text-gray-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">{message.title}</h1>
          <p className="text-white/80">{message.subtitle}</p>
        </div>

        {/* Organization Switcher or Info Box */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 text-left">
          {showOrgSwitcher ? (
            <OrganizationSwitcher organizations={organizations} />
          ) : reason === 'no_membership' ? (
            <>
              <h2 className="text-lg font-semibold text-gray-800 mb-3">Was bedeutet das?</h2>
              <p className="text-gray-600 text-sm mb-4">
                Sie sind in WorkOS einem Verein zugewiesen, aber Ihr Profil wurde noch nicht in der App angelegt. Ein
                Präsident muss Sie als Mitglied hinzufügen.
              </p>

              <h2 className="text-lg font-semibold text-gray-800 mb-3">Nächste Schritte</h2>
              <ul className="text-gray-600 text-sm space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-[var(--color-primary)] mt-1">1.</span>
                  <span>Kontaktieren Sie den Präsidenten Ihres Vereins</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[var(--color-primary)] mt-1">2.</span>
                  <span>Bitten Sie darum, als Mitglied hinzugefügt zu werden</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[var(--color-primary)] mt-1">3.</span>
                  <span>Laden Sie diese Seite neu, nachdem Sie hinzugefügt wurden</span>
                </li>
              </ul>
            </>
          ) : (
            <>
              <h2 className="text-lg font-semibold text-gray-800 mb-3">Was bedeutet das?</h2>
              <p className="text-gray-600 text-sm mb-4">
                Um die App nutzen zu können, müssen Sie Mitglied eines Vereins sein. Sie können selbst keinen Verein
                erstellen - ein Präsident muss Sie einladen.
              </p>

              <h2 className="text-lg font-semibold text-gray-800 mb-3">Nächste Schritte</h2>
              <ul className="text-gray-600 text-sm space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-[var(--color-primary)] mt-1">1.</span>
                  <span>Kontaktieren Sie den Präsidenten eines Vereins</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[var(--color-primary)] mt-1">2.</span>
                  <span>Bitten Sie um eine Einladung zum Verein</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[var(--color-primary)] mt-1">3.</span>
                  <span>Nach der Einladung laden Sie diese Seite neu</span>
                </li>
              </ul>
            </>
          )}
        </div>

        {/* User Info */}
        {userEmail && (
          <p className="text-white/60 text-sm mb-4">
            Angemeldet als: <span className="font-medium">{userEmail}</span>
          </p>
        )}

        {/* Actions */}
        <div className="space-y-3">
          {!showOrgSwitcher && (
            <button type="button" onClick={() => window.location.reload()} className="w-full btn-primary text-lg">
              Seite neu laden
            </button>
          )}
          <button
            type="button"
            onClick={() => signOut()}
            className="w-full py-3 text-white/80 font-medium hover:text-white transition-colors"
          >
            Abmelden
          </button>
        </div>
      </div>
    </div>
  );
}
